import blogPostsData from "./publishedBlogPosts.json";

export const BLOG_CONTENT_LANGUAGES = ["en", "ko", "ja"] as const;
export type BlogContentLanguage = (typeof BLOG_CONTENT_LANGUAGES)[number];

export type BlogFaq = {
  question: string;
  answer: string;
};

export type BlogSection = {
  heading: string;
  paragraphs: string[];
};

export type BlogTranslation = {
  title: string;
  description: string;
  summary: string;
  intro: string[];
  sections: BlogSection[];
  faq: BlogFaq[];
  ctaLabel?: string;
};

export type BlogPost = {
  slug: string;
  date: string;
  updated: string;
  author: string;
  tags: string[];
  translations: Record<BlogContentLanguage, BlogTranslation>;
  internalPath?: string;
  sources: Array<{ label: string; path: string }>;
  externalSources?: Array<{ title: string; url: string }>;
  relatedSlugs?: string[];
  knowledgeIds: string[];
  image?: {
    path: string;
    mediaPath?: string;
    alt: Record<BlogContentLanguage, string>;
    source?: "knowledge" | "generated";
    knowledgeId?: string;
  };
};

export const blogPosts = (blogPostsData.posts as BlogPost[]).slice().sort((a, b) =>
  b.date.localeCompare(a.date)
);

export const mergeBlogPosts = (dynamicPosts: BlogPost[]): BlogPost[] => {
  const postsBySlug = new Map<string, BlogPost>();
  for (const post of blogPosts) {
    postsBySlug.set(post.slug, post);
  }
  for (const post of dynamicPosts) {
    postsBySlug.set(post.slug, post);
  }
  return Array.from(postsBySlug.values()).sort((a, b) =>
    b.date.localeCompare(a.date)
  );
};

export const getBlogContentLanguage = (language: string): BlogContentLanguage => {
  const normalized = language.toLowerCase();
  if (normalized.startsWith("ko")) {
    return "ko";
  }
  if (normalized.startsWith("ja")) {
    return "ja";
  }
  return "en";
};

export const isPrimaryBlogLanguage = (language: string): boolean =>
  BLOG_CONTENT_LANGUAGES.includes(language as BlogContentLanguage);

export const getBlogPost = (slug?: string): BlogPost | undefined =>
  blogPosts.find((post) => post.slug === slug);

export const getBlogLabels = (language: string) => {
  const contentLanguage = getBlogContentLanguage(language);
  if (contentLanguage === "ko") {
    return {
      title: "AUTOVID 블로그",
      lead: "AI 영상 제작, 자동 쇼츠·롱폼, 마케팅 영상과 글로벌 콘텐츠 운영에 필요한 실용적인 정보를 전합니다.",
      fallback: "이 언어의 블로그는 영어 콘텐츠로 제공됩니다.",
      readMore: "글 읽기",
      back: "블로그 목록",
      faq: "자주 묻는 질문",
      sources: "출처 및 관련 공식 페이지",
      related: "관련 글",
      author: "작성자",
      published: "발행일",
      updated: "수정일",
      empty: "첫 번째 가이드를 준비하고 있습니다.",
      loading: "블로그 글을 불러오고 있습니다.",
    };
  }
  if (contentLanguage === "ja") {
    return {
      title: "AUTOVIDブログ",
      lead: "AI動画制作、自動ショート・長尺動画、マーケティング動画、グローバル運用に役立つ実践的な情報を紹介します。",
      fallback: "この言語のブログは英語で表示されます。",
      readMore: "記事を読む",
      back: "ブログ一覧",
      faq: "よくある質問",
      sources: "出典と公式ページ",
      related: "関連記事",
      author: "著者",
      published: "公開日",
      updated: "更新日",
      empty: "最初のガイドを準備しています。",
      loading: "ブログ記事を読み込んでいます。",
    };
  }
  return {
    title: "AUTOVID Blog",
    lead: "Practical guidance for AI video creation, automated Shorts and long-form workflows, marketing videos, and global content operations.",
    fallback: "Blog articles for this language are currently provided in English.",
    readMore: "Read article",
    back: "Back to blog",
    faq: "Frequently asked questions",
    sources: "Sources and official pages",
    related: "Related articles",
    author: "Author",
    published: "Published",
    updated: "Updated",
    empty: "The first guide is being prepared.",
    loading: "Loading the blog post.",
  };
};

export const formatBlogDate = (date: string, language: string): string => {
  const locale = getBlogContentLanguage(language) === "ja"
    ? "ja-JP"
    : getBlogContentLanguage(language) === "ko"
      ? "ko-KR"
      : "en-US";
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${date}T00:00:00Z`));
};
